import { persistedFindingAssetIds } from './findingOwnership';
import { redactDemoObject, redactDemoText } from './demoRedaction';

const CLIENT_DISPLAY_REDACTION = import.meta.env?.MODE === 'test'
  ? { enabled: true }
  : { env: import.meta.env };

export const SEVERITY_MAP = {
  0: 'Info',
  1: 'Low',
  2: 'Medium',
  3: 'High',
  4: 'Critical',
  'critical': 'Critical',
  'high': 'High',
  'medium': 'Medium',
  'low': 'Low',
  'info': 'Info',
};

export function normalizeSeverity(severity) {
  if (severity === null || severity === undefined) return 'Info';
  if (typeof severity === 'string') return SEVERITY_MAP[severity.toLowerCase()] ?? severity;
  if (typeof severity === 'number') return SEVERITY_MAP[severity] ?? 'Info';
  return 'Info';
}

const STATUS_NUM_MAP = {
  0: 'open',
  1: 'remediation_claimed',
  2: 'verified',
  3: 'accepted_risk',
  4: 'reopened',
  5: 'possibly_remediated',
};

const STATUS_STRING_MAP = {
  open: 'open',
  reopened: 'reopened',
  remediationclaimed: 'remediation_claimed',
  remediation_claimed: 'remediation_claimed',
  verified: 'verified',
  resolved: 'verified',
  acceptedrisk: 'accepted_risk',
  accepted_risk: 'accepted_risk',
  possiblyremediated: 'possibly_remediated',
  possibly_remediated: 'possibly_remediated',
  hidden: 'suppressed',
  suppressed: 'suppressed',
};

export function normalizeStatus(status) {
  if (status === null || status === undefined) return 'open';
  if (typeof status === 'number') return STATUS_NUM_MAP[status] ?? 'open';
  if (typeof status === 'string') {
    const key = status.trim().replace(/[-\s]/g, '_').toLowerCase();
    return STATUS_STRING_MAP[key] ?? STATUS_STRING_MAP[key.replace(/_/g, '')] ?? key;
  }
  return 'open';
}

function parseFindingDetail(f) {
  const raw = f.detail ?? f.Detail ?? f.detailJson ?? f.DetailJson ?? null;
  if (!raw) return null;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return null; }
  }
  return raw;
}

function stringify(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
}

function octetsOf(ip) {
  const parts = String(ip || '').split('.');
  if (parts.length !== 4) return null;
  const octets = parts.map(part => {
    if (!/^\d{1,3}$/.test(part)) return NaN;
    const value = Number(part);
    return value >= 0 && value <= 255 ? value : NaN;
  });
  return octets.some(Number.isNaN) ? null : octets;
}

function isInternalIPv4(ip) {
  const octets = octetsOf(ip);
  if (!octets) return false;
  const [a, b] = octets;
  return a === 10
    || a === 127
    || (a === 169 && b === 254)
    || (a === 172 && b >= 16 && b <= 31)
    || (a === 192 && b === 168)
    || (a === 100 && b >= 64 && b <= 127)
    || (a === 198 && (b === 18 || b === 19));
}

function isReservedIPv4(ip) {
  const octets = octetsOf(ip);
  if (!octets) return true;
  const [a, b, c] = octets;
  return a === 0
    || a >= 224
    || (a === 192 && b === 0 && c === 2)
    || (a === 198 && b === 51 && c === 100)
    || (a === 203 && b === 0 && c === 113);
}

function isInternalIPv6(ip) {
  const value = String(ip || '').toLowerCase();
  return value === '::1'
    || value.startsWith('fc')
    || value.startsWith('fd')
    || value.startsWith('fe80:');
}

function ipExposureScope(ip) {
  const value = String(ip || '').replace(/^\[|\]$/g, '');
  if (!value) return 'unknown';
  if (value.includes(':') && /[a-f:]/i.test(value)) {
    return isInternalIPv6(value) ? 'internal' : 'external';
  }
  if (!octetsOf(value)) return 'unknown';
  if (isInternalIPv4(value)) return 'internal';
  return isReservedIPv4(value) ? 'unknown' : 'external';
}

function extractIpAddresses(...values) {
  const text = values.map(stringify).join(' ');
  const ipv4 = text.match(/\b(?:25[0-5]|2[0-4]\d|1?\d?\d)(?:\.(?:25[0-5]|2[0-4]\d|1?\d?\d)){3}\b/g) ?? [];
  const ipv6 = text.match(/\b(?:fc|fd|fe80)[0-9a-f:]*:[0-9a-f:]*\b|::1/ig) ?? [];
  return [...new Set([...ipv4, ...ipv6])];
}

function targetExposureScope(source, detail, additionalData) {
  const targetIps = extractIpAddresses(
    source?.affectedAsset,
    source?.affectedComponent,
    source?.asset,
    source?.ipAddress,
    source?.ip,
    source?.host,
    source?.hostname,
    detail?.affectedAsset,
    detail?.asset,
    detail?.ipAddress,
    detail?.ip,
    detail?.host,
    detail?.hostname,
    additionalData?.affectedAsset,
    additionalData?.asset,
    additionalData?.ipAddress,
    additionalData?.ip,
    additionalData?.host,
    additionalData?.hostname,
  );

  if (targetIps.some(ip => ipExposureScope(ip) === 'internal')) return 'internal';
  if (targetIps.some(ip => ipExposureScope(ip) === 'external')) return 'external';
  return 'unknown';
}

function isOpenSmbFinding(source, detail, additionalData) {
  // Use native scanner identity and target fields only. Generated narratives can
  // mention SMB hardening while describing unrelated SSH/TLS findings.
  const serviceText = [
    source?.title,
    source?.module,
    source?.sourceModule,
    source?.fingerprint,
    additionalData?.serviceName,
    additionalData?.protocol,
  ].map(stringify).join(' ').toLowerCase();
  const targetText = [
    source?.affectedAsset,
    source?.affectedComponent,
    source?.asset,
    detail?.affectedAsset,
    detail?.affectedComponent,
    additionalData?.affectedAsset,
    additionalData?.affectedComponent,
    additionalData?.port,
    additionalData?.serviceName,
  ].map(stringify).join(' ').toLowerCase();

  const mentionsSmb = /\bsmb(?:v\d)?\b/.test(serviceText) || /\bmicrosoft-ds\b/.test(serviceText);
  const mentionsPort445 = /\b445\s*\/\s*tcp\b|\btcp\s*\/\s*445\b|\bport\s*445\b|[:\s]445\b/.test(targetText);
  const serviceExposure = /\b(open|exposed|publicly accessible|listening|reachable|accessible)\b/.test(serviceText);
  return mentionsSmb && (mentionsPort445 || serviceExposure);
}

function adjustedSeverity(source, detail, additionalData) {
  const severity = normalizeSeverity(source?.severity ?? source?.sev).toLowerCase();
  // An audited analyst correction is authoritative; automatic exposure heuristics
  // apply only to observations that have not been explicitly reviewed.
  if (additionalData?.portalAnalystCorrection === 'v1') return severity;
  if (severity !== 'high') return severity;
  if (!isOpenSmbFinding(source, detail, additionalData)) return severity;
  return targetExposureScope(source, detail, additionalData) === 'internal' ? 'medium' : severity;
}

function cveListOf(source, additionalData) {
  const text = [
    source?.cveId,
    source?.cve,
    source?.CveId,
    additionalData?.allCves,
    additionalData?.additionalCves,
  ].map(stringify).join(' ');
  const matches = text.match(/\bCVE-\d{4}-\d{4,}\b/gi) ?? [];
  return [...new Set(matches.map(value => value.toUpperCase()))];
}

export function normalizeFinding(f) {
  if (!f || typeof f !== 'object') return null;
  const detail = redactDemoObject(parseFindingDetail(f), CLIENT_DISPLAY_REDACTION);
  const rawAdditionalData = f.additionalData ?? f.AdditionalData ?? detail?.additionalData ?? detail?.AdditionalData ?? null;
  const additionalData = typeof rawAdditionalData === 'string'
    ? (() => { try { return JSON.parse(rawAdditionalData); } catch { return null; } })()
    : rawAdditionalData;
  const module = f.sourceModule
    ?? f.module
    ?? detail?.sourceModule
    ?? detail?.module
    ?? detail?.source
    ?? additionalData?.sourceModule
    ?? additionalData?.module
    ?? additionalData?.source
    ?? '';
  const evidenceConfidence = f.evidenceConfidence
    ?? f.evidence_confidence
    ?? additionalData?.evidence_confidence
    ?? additionalData?.evidenceConfidence
    ?? '';
  const isSuppressed = f.suppressed === true
    || f.isSuppressedInPortal === true
    || f.IsSuppressedInPortal === true
    || additionalData?.suppressed === true
    || additionalData?.isSuppressedInPortal === true;
  const cves = cveListOf(f, additionalData);
  return {
    id: f.findingId ?? f.persistedFindingId ?? f.id ?? '',
    title: redactDemoText(f.title ?? '', CLIENT_DISPLAY_REDACTION),
    description: redactDemoText(f.description ?? '', CLIENT_DISPLAY_REDACTION),
    sev: adjustedSeverity(f, detail, additionalData),
    status: isSuppressed ? 'suppressed' : normalizeStatus(f.status),
    suppressed: isSuppressed,
    isSuppressedInPortal: isSuppressed,
    module: redactDemoText(module || 'unknown', CLIENT_DISPLAY_REDACTION),
    asset: redactDemoText(f.affectedAsset ?? f.affectedComponent ?? f.asset ?? '', CLIENT_DISPLAY_REDACTION),
    type: targetExposureScope(f, detail, additionalData) === 'internal'
      ? 'Internal IP'
      : targetExposureScope(f, detail, additionalData) === 'external'
        ? 'External IP'
        : redactDemoText(f.type ?? f.Type ?? '', CLIENT_DISPLAY_REDACTION),
    assetId: f.assetId ?? f.AssetId ?? additionalData?.assetId ?? additionalData?.AssetId ?? null,
    affectedAssetId: f.affectedAssetId ?? f.AffectedAssetId ?? additionalData?.affectedAssetId ?? additionalData?.AffectedAssetId ?? null,
    assetIds: f.assetIds ?? f.AssetIds ?? additionalData?.assetIds ?? additionalData?.AssetIds ?? [],
    affectedAssetIds: persistedFindingAssetIds(f) ?? undefined,
    affectedAssetIdsProvenance: persistedFindingAssetIds(f) === null ? 'unavailable' : 'persisted',
    remediation: redactDemoText(f.remediationGuidance ?? f.remediation ?? f.rem ?? '', CLIENT_DISPLAY_REDACTION),
    ep: f.epssScore ?? f.ep ?? 0,
    cvss: f.cvssScore ?? f.cvss ?? 0,
    cve: cves[0] ?? '',
    cves,
    age: f.discoveredAt ?? f.firstSeenAt ?? f.age ?? null,
    kev: f.isKev ?? f.kev ?? false,
    mitre: f.mitreTechniques ?? f.mitre ?? [],
    detail,
    additionalData: redactDemoObject(additionalData, CLIENT_DISPLAY_REDACTION),
    evidenceConfidence,
    complianceMappings: f.complianceMappings ?? null,
    references: f.references ?? f.References ?? [],
    complianceJson: f.complianceJson ?? null,
    fingerprint: redactDemoText(f.fingerprint ?? f.Fingerprint ?? '', CLIENT_DISPLAY_REDACTION),
    lastDetectedScanJobId: f.lastDetectedScanJobId ?? null,
    duplicateGroupKey: f.duplicateGroupKey ?? f.DuplicateGroupKey ?? additionalData?.duplicateGroupKey ?? additionalData?.DuplicateGroupKey ?? '',
    duplicateGroupCount: f.duplicateGroupCount ?? f.DuplicateGroupCount ?? additionalData?.duplicateGroupCount ?? additionalData?.DuplicateGroupCount ?? 0,
    isDuplicateCandidate: f.isDuplicateCandidate ?? f.IsDuplicateCandidate ?? additionalData?.isDuplicateCandidate ?? additionalData?.IsDuplicateCandidate ?? false,
  };
}

export function evidenceConfidenceLabel(value) {
  const key = String(value || '').trim().toLowerCase();
  if (key === 'confirmed') return 'Confirmed evidence';
  if (key === 'version-banner') return 'Banner match';
  if (key === 'inferred') return 'Inferred evidence';
  return '';
}

const SEVERITY_TO_NUM = { info: 0, low: 1, medium: 2, high: 3, critical: 4 };

export function severityToApiValue(sev) {
  if (typeof sev === 'number') return sev;
  return SEVERITY_TO_NUM[(sev || '').toLowerCase()] ?? undefined;
}

const STATUS_TO_NUM = {
  open: 0,
  reopened: 4,
  remediation_claimed: 1,
  verified: 2,
  resolved: 2,
  suppressed: 3,
  accepted_risk: 3,
  possibly_remediated: 5,
};

export function statusToApiValue(status) {
  if (typeof status === 'number') return status;
  return STATUS_TO_NUM[(status || '').toLowerCase()] ?? undefined;
}
